import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useNavigate } from 'react-router-dom';
import { Item } from '../types';
import { formatGBP } from '../utils/currency';
import './Notifications.css';

// Base64 encoded simple SVG placeholder
const PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNGNUY1RjUiLz48cGF0aCBkPSJNODAgNjBIMTIwVjE0MEg4MFY2MFoiIGZpbGw9IiNEREQiLz48cGF0aCBkPSJNNjUgOTBIMTM1VjExMEg2NVY5MFoiIGZpbGw9IiNEREQiLz48L3N2Zz4=';

interface ItemNotification {
  item: Item;
  unreadCount: number;
  type: 'item' | 'rental';
  rentalId?: string;
  startDate?: string;
  endDate?: string;
  totalCost?: number;
  status?: 'upcoming' | 'active' | 'completed' | 'cancelled';
}

type ItemFilter = 'all' | 'available' | 'out' | 'overdue';

// Helper functions to determine item rental status
const isCurrentlyRented = (item: Item): boolean => {
  const now = new Date();
  return item.rentalPeriods?.some(period => {
    const start = new Date(period.startDate);
    const end = new Date(period.endDate);
    return !period.returned && start <= now && now <= end;
  }) || false;
};

const isOverdue = (item: Item): boolean => {
  const now = new Date();
  return item.rentalPeriods?.some(period => {
    const end = new Date(period.endDate);
    return !period.returned && now > end;
  }) || false;
};

const getItemStatus = (item: Item): 'available' | 'out' | 'overdue' => {
  if (isOverdue(item)) return 'overdue';
  if (isCurrentlyRented(item)) return 'out';
  return 'available';
};

const Notifications: React.FC = () => {
  const { user } = useAuth();
  const { getUserNotifications, isUsingSupabaseData } = useData();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'items' | 'rentals'>('items');
  const [itemFilter, setItemFilter] = useState<ItemFilter>('all');
  const [loading, setLoading] = useState(true);
  const [allItems, setAllItems] = useState<ItemNotification[]>([]);
  const [rentalNotifications, setRentalNotifications] = useState<ItemNotification[]>([]);

  useEffect(() => {
    const loadUserNotifications = async () => {
      if (user) {
        setLoading(true);
        try {
          const notifications = await getUserNotifications(user.id);
          
          // Always use Supabase data when connected, even if empty
          // For now, we'll show empty state since notification transformation needs more work
          setAllItems([]);
          setRentalNotifications([]);
        } catch (error) {
          console.error('Error loading user notifications:', error);
          setAllItems([]);
          setRentalNotifications([]);
        } finally {
          setLoading(false);
        }
      }
    };

    loadUserNotifications();
  }, [user, getUserNotifications]);

  const handleMarkAllAsRead = (type: 'items' | 'rentals') => {
    // TODO: Implement API call to mark all as read
    if (type === 'items') {
      setAllItems(prev => prev.map(item => ({ ...item, unreadCount: 0 })));
    } else {
      setRentalNotifications([]);
    }
  };

  // Filter items based on selected filter
  const getFilteredItems = () => {
    let filtered = allItems;
    
    if (itemFilter !== 'all') {
      filtered = allItems.filter(notification => {
        const status = getItemStatus(notification.item);
        return status === itemFilter;
      });
    }
    
    return filtered;
  };

  // Sort items so unread messages appear at the top
  const sortedItems = getFilteredItems().sort((a, b) => {
    if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
    if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
    return b.unreadCount - a.unreadCount; // Sort by unread count descending
  });

  const itemsWithUnreadMessages = allItems.filter(item => item.unreadCount > 0);
  const totalUnreadCount = allItems.reduce((total, item) => total + item.unreadCount, 0);

  // Get counts for each filter category
  const availableCount = allItems.filter(n => getItemStatus(n.item) === 'available').length;
  const outCount = allItems.filter(n => getItemStatus(n.item) === 'out').length;
  const overdueCount = allItems.filter(n => getItemStatus(n.item) === 'overdue').length;

  const handleItemClick = (notification: ItemNotification) => {
    if (notification.type === 'item') {
      navigate(`/item/${notification.item.id}/messages`);
    } else {
      navigate(`/rentals/${notification.rentalId}`);
    }
  };

  if (!user) {
    return <div>Please log in to view notifications.</div>;
  }

  if (loading) {
    return (
      <div className="notifications-page">
        <div className="notifications-header">
          <h1>Message Notifications</h1>
        </div>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          Loading notifications...
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <h1>Message Notifications</h1>
      </div>

      <div className="notifications-tabs">
        <button
          className={`tab-button ${activeTab === 'items' ? 'active' : ''}`}
          onClick={() => setActiveTab('items')}
        >
          My Items ({itemsWithUnreadMessages.length} unread)
        </button>
        <button
          className={`tab-button ${activeTab === 'rentals' ? 'active' : ''}`}
          onClick={() => setActiveTab('rentals')}
        >
          My Rentals ({rentalNotifications.length})
        </button>
      </div>

      <div className="notifications-content">
        {activeTab === 'items' && (
          <div className="notifications-section">
            <div className="section-header">
              <h2>All My Items ({totalUnreadCount} total unread)</h2>
              {totalUnreadCount > 0 && (
                <button 
                  className="mark-all-read-btn"
                  onClick={() => handleMarkAllAsRead('items')}
                >
                  Mark All as Read
                </button>
              )}
            </div>

            {/* Item Filter Buttons */}
            <div className="item-filters">
              <button
                className={`filter-btn ${itemFilter === 'all' ? 'active' : ''}`}
                onClick={() => setItemFilter('all')}
              >
                All ({allItems.length})
              </button>
              <button
                className={`filter-btn ${itemFilter === 'available' ? 'active' : ''}`}
                onClick={() => setItemFilter('available')}
              >
                Available ({availableCount})
              </button>
              <button
                className={`filter-btn ${itemFilter === 'out' ? 'active' : ''}`}
                onClick={() => setItemFilter('out')}
              >
                Rented ({outCount})
              </button>
              <button
                className={`filter-btn ${itemFilter === 'overdue' ? 'active' : ''}`}
                onClick={() => setItemFilter('overdue')}
              >
                Overdue ({overdueCount})
              </button>
            </div>
            
            {sortedItems.length === 0 ? (
              <div className="no-notifications">
                <p>
                  {itemFilter === 'all' 
                    ? "You haven't added any items yet"
                    : `No ${itemFilter} items`
                  }
                </p>
              </div>
            ) : (
              <div className="notifications-list">
                {sortedItems.map((notification) => {
                  const itemStatus = getItemStatus(notification.item);
                  return (
                    <div
                      key={notification.item.id}
                      className={`notification-item ${notification.unreadCount === 0 ? 'no-unread' : ''}`}
                      onClick={() => handleItemClick(notification)}
                    >
                      <div className="notification-image">
                        <img src={notification.item.images[0]} alt={notification.item.name} />
                        {/* Rental Status Badge */}
                        {itemStatus !== 'available' && (
                          <div className={`rental-status-badge ${itemStatus}`}>
                            {itemStatus === 'out' ? 'RENTED' : 'OVERDUE'}
                          </div>
                        )}
                        {notification.unreadCount > 0 && (
                          <div className="unread-badge">
                            <span className="notification-icon">💬</span>
                            <span className="notification-count">{notification.unreadCount}</span>
                          </div>
                        )}
                      </div>
                      <div className="notification-details">
                        <h3>{notification.item.name}</h3>
                        <p className="item-description">{notification.item.description}</p>
                        <p className="daily-rate">£{notification.item.dailyRateGBP.toFixed(2)}/day</p>
                        {notification.unreadCount > 0 ? (
                          <p className="unread-message">
                            {notification.unreadCount} unread message{notification.unreadCount !== 1 ? 's' : ''} from renter{notification.unreadCount !== 1 ? 's' : ''}
                          </p>
                        ) : (
                          <p className="no-messages">No new messages</p>
                        )}
                        {/* Show rental info for rented/overdue items */}
                        {itemStatus !== 'available' && notification.item.rentalPeriods && notification.item.rentalPeriods.length > 0 && (
                          <p className="rental-info">
                            Rented by {notification.item.rentalPeriods[0].renterName} 
                            ({new Date(notification.item.rentalPeriods[0].startDate).toLocaleDateString()} - {new Date(notification.item.rentalPeriods[0].endDate).toLocaleDateString()})
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'rentals' && (
          <div className="notifications-section">
            <div className="section-header">
              <h2>Rentals with unread messages from owners</h2>
              {rentalNotifications.length > 0 && (
                <button 
                  className="mark-all-read-btn"
                  onClick={() => handleMarkAllAsRead('rentals')}
                >
                  Mark All as Read
                </button>
              )}
            </div>
            
            {rentalNotifications.length === 0 ? (
              <div className="no-notifications">
                <p>No unread messages for your rentals</p>
              </div>
            ) : (
              <div className="notifications-list">
                {rentalNotifications.map((notification) => (
                  <div
                    key={notification.rentalId}
                    className="notification-item"
                    onClick={() => handleItemClick(notification)}
                  >
                    <div className="notification-image">
                      <img src={notification.item.images[0]} alt={notification.item.name} />
                      <div className="unread-badge">
                        <span className="notification-icon">💬</span>
                        <span className="notification-count">{notification.unreadCount}</span>
                      </div>
                    </div>
                    <div className="notification-details">
                      <h3>{notification.item.name}</h3>
                      <p className="rental-period">
                        {notification.startDate && notification.endDate && 
                          `${new Date(notification.startDate).toLocaleDateString()} - ${new Date(notification.endDate).toLocaleDateString()}`
                        }
                      </p>
                      <p className="rental-cost">
                        {notification.totalCost && 
                          `£${(notification.totalCost * 0.0001).toFixed(2)}`
                        }
                      </p>
                      <p className="unread-message">
                        {notification.unreadCount} unread message{notification.unreadCount !== 1 ? 's' : ''} from owner
                      </p>
                    </div>
                    {notification.status && (
                      <div className={`rental-status ${notification.status}`}>
                        {notification.status.charAt(0).toUpperCase() + notification.status.slice(1)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications; 